import { ref } from 'vue'
import type { ApiService } from '@nuxtDatakodeUtils/utils/api.service'
import type { I3pService } from '@nuxtDatakodeUtils/services/i3p.service'
import { fromLonLat } from 'ol/proj.js'
import { Application } from '~/models/Application'
import { ApplicationTable } from '~/models/ApplicationTable'
import { MainI3pService } from '~/services/main.i3p.service'
import { TableI3pService } from '~/services/table.i3p.service'
import { TableNotFoundError } from '~/errors/TableNotFoundError'

type NavigationMode = Datakode.LaravelDatakodeUtils.Enum.NavigationMode

type GenericTableData = PildomModules.Generic.Data.GenericTableData
type GenericApplicationData = PildomModules.Generic.Data.GenericApplicationData

export class ApplicationService {
    private readonly $api: ApiService
    private readonly $i18n: object
    public application: Application
    public applicationRef: Ref<Application>
    public currentTableRef: Ref<ApplicationTable>
    public tables: ApplicationTable[] = []
    public mainI3pService: MainI3pService
    public tableI3pServices: { [key: string]: TableI3pService } = reactive({})
    public loading: boolean = true

    constructor(api: ApiService, i18n: object) {
        this.$api = api
        this.$i18n = i18n

        this.applicationRef = ref(null)
        this.currentTableRef = ref(null)
        this.mainI3pService = new MainI3pService()
    }

    init(applicationSlug: string): Promise {
        return new Promise<void>((resolve, reject) => {
            (new Application()).get(applicationSlug).then((application) => {
                if (!application) {
                    reject(new Error('Application not found'))
                }
                this.application = application as Application
                this.applicationRef.value = this.application
                this.tables = application?.data?.tables.map((item: GenericTableData) => {
                    const table = new ApplicationTable(item)
                    table.setApplication(application)
                    const tableI3pService = new TableI3pService(table)
                    if (table?.data?.slug)
                        this.tableI3pServices[table.data.slug] = tableI3pService

                    return table
                })
                this.loading = false
                resolve()
            })
        })
    }

    setCurrentTable(tableSlug: string): Promise {
        return new Promise<void>((resolve, reject) => {
            const table = this.tables.find((value: ApplicationTable) => {
                return value.data.slug === tableSlug
            })
            if (!table) {
                reject(new TableNotFoundError(`Table ${tableSlug} not found in application ${this.application.data.name}`))
            }

            this.currentTableRef.value = table
            resolve()
        })
    }

    isCurrentTable(table: ApplicationTable): boolean {
        return this.currentTableRef.value.data.slug === table.data.slug
    }

    getTableI3pService(table: ApplicationTable): TableI3pService {
        return this.tableI3pServices[table.data.slug]
    }

    getMainI3PService(): I3pService<any> {
        return new MainI3pService()
    }

    getMapLayers(): Array {
        const mapLayers = this.application.data?.map_layers

        return mapLayers.reverse()
        // if (mapLayers?.length) {
        //     return mapLayers.map((layer, i) => {
        //         return {
        //             ...layer,
        //             zIndex: (mapLayers.length - i),
        //         }
        //     })
        // }
        // return []
    }

    getBBox(): { x_min: number, x_max: number, y_min: number, y_max: number } {
        const data = this.application.data as GenericApplicationData
        const pt1 = fromLonLat([data.longitude_min, data.latitude_min])
        const pt2 = fromLonLat([data.longitude_max, data.latitude_max])
        return {
            x_min: pt1[0],
            x_max: pt2[0],
            y_min: pt1[1],
            y_max: pt2[1],
        }
    }

    setNavigationMode(navigationMode: NavigationMode): void {
        for (const tableName in this.tableI3pServices) {
            (this.tableI3pServices[tableName] as TableI3pService).setNavigationMode(navigationMode)
        }
    }
}
